import type { PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import { buildCancellationPolicies, buildPartiallyRefundableCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { sum } from 'lib/array/arrayUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import uuidV4 from 'lib/string/uuidV4Utils'
import config from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { applyChannelMarkupAndRoundUpToTen, getChannelMarkupDetails, getMemberPriceChannelMarkupDetails } from 'lib/channelMarkup/channelMarkup'

export { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'

function bedGroupsMap(bedGroup: PublicOfferV2.BedGroup): App.RoomBedGroups {
  return {
    id: bedGroup.id,
    description: bedGroup.description,
    configuration: bedGroup.configuration.map(bedGroupsConfigurationMap),
  }
}

function bedGroupsConfigurationMap(configuration: PublicOfferV2.Configuration): App.BedGroupsConfiguration {
  return {
    type: configuration.type,
    size: configuration.size,
    quantity: configuration.quantity,
  }
}

export function mapBedbankRoomBoardCode(boardCode: string | undefined): string {
  switch (boardCode) {
    case 'Daily breakfast':
      return 'BB'
    case 'Daily breakfast & choice of daily lunch or dinner':
      return 'HB'
    case 'Daily breakfast, lunch & dinner':
      return 'FB'
    case 'All inclusive: Daily breakfast, lunch, dinner & drinks':
      return 'AL'
    case 'Room only':
      return 'RO'
    default:
      return 'LE'
  }
}

function getRateInclusions(serverRate: PublicOfferV2.BedbankRate): Array<App.OfferInclusion> {
  return serverRate.inclusions?.map(inclusion => {
    return {
      id: uuidV4(),
      description: inclusion.text,
      symbol: determineOfferFeatureSymbol(inclusion.icon || inclusion.text),
    }
  }) ?? []
}

export default function bedbankRateMap({
  checkIn,
  checkOut,
  timezone,
  rate,
  room,
  offerId,
  channelMarkup,
}: {
  checkIn: string
  checkOut: string
  timezone?: string
  rate: PublicOfferV2.BedbankRate
  room: App.BedbankPackage
  offerId?: string
  channelMarkup?: App.ChannelMarkup
}): App.BedbankRate {
  let cancellationPolicy: App.CancellationPolicy = { policies: [], description: [] }
  const locale = getCurrentIntl().locale

  // Channel markup (LPP): uplift sell prices (rounded to ten, matching svc-order); taxes,
  // fees and RRP value untouched. No-op when not applicable.
  const markupDetails = getChannelMarkupDetails(offerId ?? '', OFFER_TYPE_BED_BANK, channelMarkup)
  const memberMarkupDetails = getMemberPriceChannelMarkupDetails(offerId ?? '', OFFER_TYPE_BED_BANK, channelMarkup)
  const markup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, markupDetails)
  const memberMarkup = (value: number) => applyChannelMarkupAndRoundUpToTen(value, memberMarkupDetails)
  // Derive the markup base from exclusive + taxesAndFees so that propertyFees is
  // never included in the roundUpToNearestTen calculation, matching svc-order.
  const inclusiveBase = rate.totals.exclusive + rate.totals.taxesAndFees
  const markedInclusive = markup(inclusiveBase)
  const markedMemberInclusive = rate.totals.luxPlusInclusive ? memberMarkup(rate.totals.luxPlusInclusive) : 0

  if (timezone) {
    cancellationPolicy = rate.partiallyRefundable
      ? buildPartiallyRefundableCancellationPolicies(
          {
            checkIn,
            checkOut,
            policies: rate.cancellationPolicies,
            nonRefundableDates: rate.nonRefundableDateRanges,
          },
          { timezone, locale, midnightWrap: false },
        )
      : buildCancellationPolicies(rate.cancellationPolicies, {
          timezone,
          regionCode: rate.regionCode,
          currencyCode: rate.currencyCode,
          locale,
          midnightWrap: false,
        })
  }

  // Common properties shared between both payment method cases
  const baseRate = {
    availableRooms: rate.availableRooms,
    bedGroups: rate.bedGroups.map(bedGroupsMap),
    id: rate.id,
    optionId: rate.optionId,
    promotionId: rate.promotionId ?? undefined,
    roomId: room.roomId,
    room,
    refundable: rate.refundable,
    partiallyRefundable: rate.partiallyRefundable,
    isFlightBundle: rate.isFlightBundle,
    mobilePromotion: rate.mobilePromotion,
    groupId: rate.groupId ?? undefined,
    corporate: rate.corporate,
    occupancyPricing: rate.occupancyPricing.map(op => {
      const opInclusiveBase = op.exclusive + op.taxesAndFees
      const inclusive = markup(opInclusiveBase)
      const memberInclusive = op.luxPlusInclusive ? memberMarkup(op.luxPlusInclusive) : 0
      const otherFees = sum(op.fees, fee => fee.amount)
      return {
        ...op,
        occupancy: op.occupancy,
        exclusive: markup(op.exclusive),
        inclusive,
        taxesAndFees: op.taxesAndFees,
        salesTax: op.salesTax,
        fees: op.fees,
        memberExclusive: op.luxPlusExclusive ? memberMarkup(op.luxPlusExclusive) : 0,
        memberInclusive,
        total: inclusive + otherFees,
        memberTotal: op.luxPlusInclusive ? memberInclusive + otherFees : undefined,
      }
    }),
    totals: {
      exclusive: markup(rate.totals.exclusive),
      inclusive: markedInclusive,
      propertyFees: rate.totals.propertyFees,
      taxesAndFees: rate.totals.taxesAndFees,
      memberExclusive: rate.totals.luxPlusExclusive ? memberMarkup(rate.totals.luxPlusExclusive) : 0,
      memberInclusive: markedMemberInclusive,
      total: markedInclusive + (rate.totals.propertyFees ?? 0),
      memberTotal: rate.totals.luxPlusInclusive ? markedMemberInclusive + (rate.totals.propertyFees ?? 0) : undefined,
    },
    nights: rate.duration,
    cancellationPolicies: rate.cancellationPolicies,
    value: rate.value,
    // Recompute "% off" from the marked price so the badge isn't overstated; clamp at 0 so a
    // marked price above RRP never renders a negative discount. Keep the server value when no
    // markup applies.
    discount: markupDetails.isApplied
      ? Math.max(0, getValueOffPercent(rate.value, markedInclusive))
      : Math.floor(rate.discount * 100),
    memberDiscount: memberMarkupDetails.isApplied && rate.totals.luxPlusInclusive
      ? Math.max(0, getValueOffPercent(rate.value, markedMemberInclusive))
      : (rate.luxPlusDiscount ? Math.floor(rate.luxPlusDiscount * 100) : 0),
    cancellationPolicy,
    margin: rate.margin,
    marginAud: rate.marginAud,
    mx: rate.mx,
    supplier: rate.supplier,
    supplierCode: rate.supplierCode,
    boardCode: mapBedbankBoardCode(rate.boardCode),
    inclusions: getRateInclusions(rate),
    paymentScheduleInfo: (config.PAYMENT_SCHEDULE_LPP_ENABLED && rate.paymentSchedule) ? {
      staticSchedule: {
        templateId: rate.paymentSchedule.templateId,
        supplierPaymentNumberOfDays: rate.paymentSchedule.supplierDueDays,
      },
    } : undefined,
    relations: {
      paymentMethods: rate.relations?.paymentMethod ?? [],
    },
  }

  // Use discriminated union to ensure type safety
  if (rate.paymentMethod === 'propertyCollect') {
    return {
      ...baseRate,
      paymentMethod: 'propertyCollect',
      calculationMethod: rate.calculationMethod,
      payAtPropertyTotals: rate.payAtPropertyTotals!, // Required when propertyCollect
    }
  }

  return {
    ...baseRate,
    paymentMethod: 'leCollect',
    calculationMethod: rate.calculationMethod,
  }
}
